import type { Office, Segment } from "./settingsTree";

import { segmentKeys } from "./settingsTree";

export type SegmentKey = (typeof segmentKeys)[number];

/** The office field each segment dropdown narrows the later lists by */
const officeFields: Record<SegmentKey, keyof Office> = {
	brandId: "brandId",
	channelId: "channelId",
	marketerId: "marketerId",
	usStateId: "stateId",
	branchId: "id",
};

/**
 * The segment after picking or clearing one dropdown.
 * Picking an office fills in its own brand, channel, marketer and state, since that's the segment its settings are read for.
 * Otherwise the earlier picks stay, and each later pick stays while its dropdown still offers it.
 */
export function changePick(segment: Segment, key: SegmentKey, id: number | null | undefined, offices: Office[] = []) {
	const office = key === "branchId" && id != null ? offices.find((o) => o.id === id) : undefined;
	if (office)
		return {
			brandId: office.brandId,
			channelId: office.channelId,
			marketerId: office.marketerId,
			usStateId: office.stateId,
			branchId: office.id,
		};

	const index = segmentKeys.indexOf(key);
	const next: Segment = { [key]: id ?? undefined };
	for (const earlier of segmentKeys.slice(0, index)) next[earlier] = segment[earlier];

	const activeOffices = offices.filter((o) => o.Active ?? true);
	for (const later of segmentKeys.slice(index + 1)) {
		const kept = segment[later];
		if (kept != null && isOffered(later, kept, next, activeOffices)) next[later] = kept;
	}
	return next;
}

/**
 * Whether the dropdown lists the id, the way useSegmentFilterCascaded builds it:
 * from the active offices that match the picks before it, or every item when no office matches them.
 */
function isOffered(key: SegmentKey, id: number, picks: Segment, activeOffices: Office[]) {
	const earlier = segmentKeys.slice(0, segmentKeys.indexOf(key));
	const matching = activeOffices.filter((o) =>
		earlier.every((k) => picks[k] == null || o[officeFields[k]] === picks[k]),
	);
	return !matching.length || matching.some((o) => o[officeFields[key]] === id);
}
